import {
    data,
    LinksFunction,
    LoaderFunctionArgs,
    redirect,
    isRouteErrorResponse,
    Links,
    Meta,
    MetaFunction,
    Outlet,
    Scripts,
    ScrollRestoration,
    useRouteError,
    useRouteLoaderData,
} from 'react-router';
import dayjs from 'dayjs';
import pt from 'dayjs/locale/pt';
import utc from 'dayjs/plugin/utc';
import rcPickerStyles from 'rc-picker/assets/index.css?url';
import { PropsWithChildren, Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import 'react-toastify/ReactToastify.css';
import { getUser } from '~/helpers/user';
import styles from '~/tailwind.css?url';
import gridStyles from 'react-grid-layout/css/styles.css?url';
import resizableStyles from 'react-resizable/css/styles.css?url';
import * as Sentry from '@sentry/react-router';
import getApolloClient from './apollo/client';
import {
    HASURA_GRAPHQL_URI,
    HASURA_GRAPHQL_WS_URI,
    NODE_ENV,
    INSTANCE_ID,
    POLL_INTERVAL,
    PROXY_ENABLED,
    PROXY_TARGET,
    WEBAPP_ROUTE_PREFIX,
    VITE_PUBLIC_POSTHOG_HOST,
    VITE_PUBLIC_POSTHOG_TOKEN,
    KONG_API_KEY,
    KONG_API_BASE_URL,
    SENTRY_DSN,
    REVERB_APP_KEY,
    REVERB_HOST,
    REVERB_PORT,
    REVERB_SCHEME,
} from './constants';
import { userPrefsCookie } from './cookies.server';
import { LanguagesDocument, LanguagesQuery } from './hasura/__generated__';
import { getImageSrc } from './helpers/image';
import { commitSession, destroySession, getSession } from './sessions';
import { getCookieValue } from './helpers/cookies';

dayjs.extend(utc);
dayjs.locale(pt);

export const links: LinksFunction = () => [
    // ARCGIS Stylesheets
    {
        rel: 'stylesheet',
        href: 'https://js.arcgis.com/calcite-components/3.0.3/calcite.css',
    },
    {
        rel: 'stylesheet',
        href: 'https://js.arcgis.com/map-components/4.32/arcgis-map-components.css',
    },
    { rel: 'stylesheet', href: rcPickerStyles },
    { rel: 'stylesheet', href: styles },
    { rel: 'stylesheet', href: gridStyles },
    { rel: 'stylesheet', href: resizableStyles },
    {
        rel: 'icon',
        href: getImageSrc('/favicon.ico'),
    },
];

export async function loader({ request }: LoaderFunctionArgs) {
    const url = new URL(request.url);
    const cookieHeader = request.headers.get('Cookie');

    const session = await getSession(cookieHeader);
    const currentToken = session.get('token');

    const instancePrefs = (await userPrefsCookie.parse(cookieHeader)) || {};

    // Fetch instance default language + available languages (cached in instancePrefs)
    if (!instancePrefs.availableLanguages?.length) {
        const ssrClientAnon = getApolloClient();
        const { data: hasuraLanguages } =
            await ssrClientAnon.query<LanguagesQuery>({
                query: LanguagesDocument,
            });

        const {
            available_languages: hasuraAvailableLanguages,
            default_language: hasuraDefaultLanguage,
        } = hasuraLanguages.getLanguages ?? {};

        instancePrefs.availableLanguages = hasuraAvailableLanguages || [];
        instancePrefs.defaultLanguage = hasuraDefaultLanguage
            ? hasuraDefaultLanguage.split('.')[0].replace('_', '-')
            : 'pt-PT';
    }

    const defaultLanguage = instancePrefs.defaultLanguage ?? 'pt-PT';

    // Convert locale cookie (e.g. 'fr_FR.UTF-8' set by Legacy PHP) to hyphenated code (e.g. 'fr-FR')
    const cookieLocale = getCookieValue(cookieHeader, 'locale');
    let cookieLocaleCode: string | null = null;
    if (cookieLocale) {
        const [base] = decodeURIComponent(cookieLocale).split('.');
        cookieLocaleCode = base.replace('_', '-');
    }

    // Language priority:
    // - Not logged in → always use instance default language
    // - Logged in → locale cookie (updated by webapp or Legacy PHP), fallback to session, fallback to default
    const effectiveLocale = currentToken
        ? (cookieLocaleCode ?? session.get('locale') ?? defaultLanguage)
        : defaultLanguage;

    // Keep session in sync with the locale cookie (e.g. when Legacy PHP changes the language)
    if (currentToken && session.get('locale') !== effectiveLocale) {
        session.set('locale', effectiveLocale);
    }

    const newToken = getCookieValue(cookieHeader, '__AppSession');
    if (newToken && newToken !== currentToken) {
        const user = getUser(newToken);
        session.set('token', newToken);
        session.set('userId', user.id);
        session.set('username', user.name);
        session.set('locale', effectiveLocale);

        if (
            WEBAPP_ROUTE_PREFIX.length > 0 &&
            url.pathname.startsWith(WEBAPP_ROUTE_PREFIX)
        ) {
            url.pathname = url.pathname.slice(WEBAPP_ROUTE_PREFIX.length);
        }

        const redirectHeaders = new Headers();
        redirectHeaders.append('Set-Cookie', await commitSession(session));
        redirectHeaders.append(
            'Set-Cookie',
            await userPrefsCookie.serialize(instancePrefs)
        );

        // redirect to the same page without search params
        return redirect(url.pathname, { headers: redirectHeaders });
    }

    const ROUTE_PREFIX_ELEMENTS = WEBAPP_ROUTE_PREFIX.split('/');
    const ROUTE_PATH_ELEMENTS = url.pathname
        .split('/')
        .splice(ROUTE_PREFIX_ELEMENTS.length)
        .filter(Boolean)
        .map((e) => e.toLowerCase());

    // Public pages
    const isDashboardPage =
        ROUTE_PATH_ELEMENTS[0] === 'dashboards' &&
        ROUTE_PATH_ELEMENTS[1] === 'visioon' &&
        ROUTE_PATH_ELEMENTS.length > 2;

    const isAuthPage = ['login'].includes(ROUTE_PATH_ELEMENTS[0]);

    const isAboutAvPage =
        ROUTE_PATH_ELEMENTS[0] === 'av' && ROUTE_PATH_ELEMENTS[1] === 'sobre';

    if (
        !isAuthPage &&
        !isDashboardPage &&
        !isAboutAvPage &&
        !currentToken &&
        url.pathname !== `${WEBAPP_ROUTE_PREFIX}/not-authorized`
    ) {
        return redirect('/not-authorized', {
            headers: {
                'Set-Cookie': await destroySession(session),
            },
        });
    }

    // Theme Logic

    const headers = new Headers();
    headers.append('Set-Cookie', await commitSession(session));
    headers.append(
        'Set-Cookie',
        await userPrefsCookie.serialize(instancePrefs)
    );

    return data(
        {
            isLoggedIn: !!currentToken,
            userId: session.get('userId'),
            username: session.get('username'),
            locale: effectiveLocale,
            availableLanguages: instancePrefs.availableLanguages,
            ENV: {
                NODE_ENV,
                INSTANCE_ID,
                HASURA_GRAPHQL_URI,
                HASURA_GRAPHQL_WS_URI,
                WEBAPP_ROUTE_PREFIX,
                PROXY_ENABLED,
                PROXY_TARGET,
                POLL_INTERVAL,
                VITE_PUBLIC_POSTHOG_TOKEN,
                VITE_PUBLIC_POSTHOG_HOST,
                KONG_API_KEY,
                KONG_API_BASE_URL,
                SENTRY_DSN,
                REVERB_APP_KEY,
                REVERB_HOST,
                REVERB_PORT,
                REVERB_SCHEME,
            },
        },
        {
            headers,
        }
    );
}

export const meta: MetaFunction<typeof loader> = () => [
    { title: 'Navia Solutions' },
    { name: 'description', content: 'Navia Solutions' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { charset: 'utf-8' },
];

export function ErrorBoundary() {
    const error = useRouteError();
    useEffect(() => {
        Sentry.captureException(error);
    }, [error]);

    if (isRouteErrorResponse(error)) {
        return (
            <div>
                <h1>
                    {error.status} {error.statusText}
                </h1>
                <p>{error.data}</p>
            </div>
        );
    }
    if (error instanceof Error) {
        return (
            <div>
                <h1>Error</h1>
                <p>{error.message}</p>
                <p>The stack trace is:</p>
                <pre>{error.stack}</pre>
            </div>
        );
    }
    return <h1>Unknown Error</h1>;
}

export function Layout({ children }: PropsWithChildren) {
    const loaderData = useRouteLoaderData<typeof loader>('root');
    const { i18n } = useTranslation();

    const { ENV, locale } = loaderData || {};

    useEffect(() => {
        i18n.changeLanguage(locale ?? 'pt-PT');
    }, [locale, i18n]);

    const theme = 'light';

    return (
        <html lang={locale} dir={i18n.dir()} data-theme={theme}>
            <head>
                {/* Static tags ensure charset and viewport are in the initial HTML byte,
                    preventing the browser from defaulting to 980px before hydration. */}
                <meta charSet="utf-8" />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1"
                />
                <Meta />
                <Links />
            </head>
            <body>
                <script
                    dangerouslySetInnerHTML={{
                        __html: `window.ENV = ${JSON.stringify(ENV)};`,
                    }}
                />
                {children}
                <ScrollRestoration />
                <Scripts />
            </body>
        </html>
    );
}

export default function App() {
    return (
        <Suspense>
            <Outlet />
        </Suspense>
    );
}
